// A person's time zone, and a time labeled with it.
//
// EVERY TIME A PERSON READS IS IN THEIR ACCOUNT'S ZONE, AND SAYS WHICH. The
// zone is chosen when the account is made and changed in the account's
// settings, never worked out from the device — a manager who travels with a
// phone still reads a zone they chose — and every time carries a clear zone
// label, 10 AM MDT for instance. A league's zone decides WHEN things happen
// (a waiver run at 3 AM, the day a lock falls on); nobody reads a time in it
// unless their account has none yet (B-127).

import { z } from 'zod';

/** Whether `zone` names an IANA zone this runtime can format in. */
export function isTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * An IANA zone, as an account holds it ("America/Denver", "Pacific/Auckland").
 * Checked against the runtime's own zone table, so a zone the server cannot
 * format in is refused at the boundary rather than failing a screen later.
 */
export const TimeZone = z
  .string()
  .trim()
  .min(1, 'Pick a time zone.')
  .max(64)
  .refine(isTimeZone, 'Pick a time zone from the list.');
export type TimeZone = z.infer<typeof TimeZone>;

/**
 * Locales tried for a zone's short name, in order. `en-US` names only North
 * American zones ("MDT"); for the rest it says "GMT+13", and the English
 * locale of the place names it the way people there write it ("NZDT",
 * "AEST", "BST"). Past the list, the offset is still an honest label.
 */
const NAMING_LOCALES = ['en-US', 'en-NZ', 'en-AU', 'en-GB', 'en-IE', 'en-IN', 'en-ZA', 'en-SG'];

/**
 * The zone's short name at `at` — "MDT" in July and "MST" in January, since
 * daylight saving is part of the name: "MDT", "NZDT", "GMT+5:30".
 */
export function zoneAbbreviation(zone: string, at: number | string | Date): string {
  const date = at instanceof Date ? at : new Date(at);
  let fallback = '';
  for (const locale of NAMING_LOCALES) {
    const name =
      new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'short' })
        .formatToParts(date)
        .find((p) => p.type === 'timeZoneName')?.value ?? '';
    if (fallback === '') fallback = name;
    // "GMT+13" is a locale with no name for the zone; a bare "GMT" is London's
    // own winter name and is kept.
    if (name !== '' && !/^(GMT|UTC)[+-]/.test(name)) return name;
  }
  return fallback;
}

/**
 * `at` formatted in `zone` with `options`, then the zone's short name:
 * "Sun 11:00 AM MDT". The label is appended rather than asked of
 * `timeZoneName`, because `en-US` would print "GMT+13" for Auckland.
 */
export function formatInZone(
  at: number | string | Date,
  zone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = at instanceof Date ? at : new Date(at);
  const text = new Intl.DateTimeFormat('en-US', { ...options, timeZone: zone }).format(date);
  return `${text} ${zoneAbbreviation(zone, date)}`;
}

/** The label a zone goes by in a picker: "America/Denver (MDT)". */
export function zoneOptionLabel(zone: string, at: number | string | Date): string {
  return `${zone.replace(/_/g, ' ')} (${zoneAbbreviation(zone, at)})`;
}

/** The wall-clock minute of the day at `at` in `zone`, 0 to 1439. */
function minuteOfDay(zone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at);
  const read = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return (read('hour') % 24) * 60 + read('minute');
}

/**
 * When a daily stop a league's rules set — the draft clock's overnight pause
 * (C7, B-341), "00:00" to "08:00" in its zone, or one that crosses midnight —
 * ends, if `at` falls inside it; null when it does not, when the stop starts
 * and ends together, or for a zone this runtime does not know.
 *
 * FOR SAYING SO, NOT FOR RUNNING A CLOCK: the room's own arithmetic is the
 * live worker's (`overnight.ts`). Here a night the clocks change is corrected
 * once, which is all a sentence needs.
 */
export function dailyStopEnds(
  stop: { from: string; to: string },
  zone: string,
  at: number,
): number | null {
  if (stop.from === stop.to || !isTimeZone(zone)) return null;
  const minutes = (t: string): number => {
    const [h, m] = t.split(':').map(Number) as [number, number];
    return h * 60 + m;
  };
  const from = minutes(stop.from);
  const to = minutes(stop.to);
  const m = minuteOfDay(zone, new Date(at));
  const inside = from < to ? m >= from && m < to : m >= from || m < to;
  if (!inside) return null;
  const ahead = (to - m + 1440) % 1440;
  let end = at - (at % 60_000) + ahead * 60_000;
  // A NIGHT THE CLOCKS CHANGE puts the guess an hour out; the wall clock at
  // the guess says by how much.
  let off = minuteOfDay(zone, new Date(end)) - to;
  if (off > 720) off -= 1440;
  if (off < -720) off += 1440;
  end -= off * 60_000;
  return end;
}
